import { useEffect } from "react";
import { Shield } from "lucide-react";
import "../styles/privacy-policy.css";

export default function PrivacyPolicyPage() {
  // Quand on arrive sur la page : remonter tout en haut
  useEffect(() => {
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: "auto",
    });
  }, []);

  const handleScroll = (event, targetId) => {
    event.preventDefault();

    const element = document.getElementById(targetId);
    if (!element) return;

    element.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
  };

  return (
    <div className="privacy-page">
      {/* HEADER GLOBAL */}
      <header className="privacy-header">
        <img
          src="/logo_nms06.png"
          alt="Nouvelles Méthodes Solidaires 06"
          className="privacy-logo"
        />
        <Shield className="privacy-icon" />
        <h1 className="privacy-title">Politique de Confidentialité &amp; RGPD</h1>
        <p className="privacy-date">Dernière mise à jour : décembre 2025</p>
      </header>

      {/* LAYOUT : sidebar + contenu */}
      <div className="privacy-layout">
        {/* SOMMAIRE GAUCHE */}
        <aside className="privacy-sidebar">
          <nav>
            <h2 className="privacy-sidebar-title">Sommaire</h2>
            <ul>
              <li>
                <a
                  href="#section-1"
                  onClick={(e) => handleScroll(e, "section-1")}
                >
                  1. Responsable du traitement
                </a>
              </li>
              <li>
                <a
                  href="#section-2"
                  onClick={(e) => handleScroll(e, "section-2")}
                >
                  2. Données collectées
                </a>
              </li>
              <li>
                <a
                  href="#section-3"
                  onClick={(e) => handleScroll(e, "section-3")}
                >
                  3. Utilisation des données
                </a>
              </li>
              <li>
                <a
                  href="#section-4"
                  onClick={(e) => handleScroll(e, "section-4")}
                >
                  4. Données enfants
                </a>
              </li>
              <li>
                <a
                  href="#section-5"
                  onClick={(e) => handleScroll(e, "section-5")}
                >
                  5. IA &amp; automatisation
                </a>
              </li>
              <li>
                <a
                  href="#section-6"
                  onClick={(e) => handleScroll(e, "section-6")}
                >
                  6. Conservation
                </a>
              </li>
              <li>
                <a
                  href="#section-7"
                  onClick={(e) => handleScroll(e, "section-7")}
                >
                  7. Vos droits
                </a>
              </li>
              <li>
                <a
                  href="#section-8"
                  onClick={(e) => handleScroll(e, "section-8")}
                >
                  8. Cookies
                </a>
              </li>
              <li>
                <a
                  href="#section-9"
                  onClick={(e) => handleScroll(e, "section-9")}
                >
                  9. Sécurité
                </a>
              </li>
              <li>
                <a
                  href="#section-10"
                  onClick={(e) => handleScroll(e, "section-10")}
                >
                  10. Contact
                </a>
              </li>
            </ul>
          </nav>
        </aside>

        {/* CONTENU PRINCIPAL */}
        <main className="privacy-container">
          <div className="privacy-content">
            {/* 1 */}
            <section id="section-1" className="privacy-section">
              <h2>1. Responsable du traitement</h2>
              <p>Le traitement des données personnelles est assuré par :</p>
              <ul>
                <li>
                  <strong>Nouvelles Méthodes Solidaires (NMS06)</strong>
                </li>
                <li>
                  251 avenue de la Porte des Alpes, Plan-du-Var, 06670 Levens
                </li>
                <li>
                  Email :{" "}
                  <a href="mailto:secretariat@nms06.fr">
                    secretariat@nms06.fr
                  </a>
                </li>
                <li>Téléphone : 06 45 42 42 33</li>
                <li>Président : Nourrédine DEBBARI</li>
              </ul>
            </section>

            {/* 2 */}
            <section id="section-2" className="privacy-section">
              <h2>2. Données collectées</h2>
              <p>Nous collectons les catégories de données suivantes :</p>

              <h3>2.1. Données fournies volontairement</h3>
              <ul>
                <li>Nom et prénom</li>
                <li>Adresse email</li>
                <li>Numéro de téléphone</li>
                <li>Adresse postale</li>
                <li>Âge (notamment pour les services du Père Noël)</li>
                <li>Messages envoyés via nos formulaires</li>
                <li>Images, photos, vidéos ou fichiers transmis</li>
                <li>Identifiants de connexion (mot de passe hashé)</li>
              </ul>

              <h3>2.2. Données collectées automatiquement</h3>
              <ul>
                <li>Adresse IP</li>
                <li>Cookies techniques, statistiques et marketing</li>
                <li>Données de navigation (analytics)</li>
                <li>Géolocalisation approximative</li>
              </ul>

              <h3>2.3. Paiements</h3>
              <ul>
                <li>Prestataire : PayPal</li>
                <li>Aucune donnée bancaire n’est stockée par NMS06</li>
              </ul>
            </section>

            {/* 3 */}
            <section id="section-3" className="privacy-section">
              <h2>3. Utilisation des données</h2>
              <p>Les données sont utilisées pour :</p>
              <ul>
                <li>Gérer les comptes utilisateurs</li>
                <li>Assurer les adhésions, dons et paiements</li>
                <li>Permettre la réservation ou l&apos;achat de services</li>
                <li>Gérer les animations et messages du Père Noël 🎅</li>
                <li>Envoyer des informations ou newsletters</li>
                <li>Assurer le support et la communication avec l’association</li>
                <li>Améliorer nos services et notre site internet</li>
                <li>Produire des statistiques anonymisées</li>
              </ul>
            </section>

            {/* 4 */}
            <section id="section-4" className="privacy-section">
              <h2>4. Données concernant les enfants</h2>
              <p>
                Certains services destinés aux enfants (chat Père Noël, messages
                audio ou vidéo) peuvent nécessiter la collecte de :
              </p>
              <ul>
                <li>Prénom de l’enfant</li>
                <li>Âge</li>
                <li>Message ou liste de souhaits</li>
                <li>Éventuels fichiers envoyés par les parents</li>
              </ul>
              <p>
                Ces données sont strictement confidentielles et supprimées sur
                simple demande d’un parent ou responsable légal.
              </p>
            </section>

            {/* 5 */}
            <section id="section-5" className="privacy-section">
              <h2>5. Traitements via Intelligence Artificielle (IA)</h2>
              <p>
                Certaines fonctionnalités utilisent des services externes
                d’Intelligence Artificielle (génération de texte, audio,
                animation, support automatisé). Les prestataires peuvent varier
                dans le temps pour garantir les meilleurs services.
              </p>
              <p>NMS06 garantit que :</p>
              <ul>
                <li>
                  Les IA utilisées sont traitées comme des sous-traitants
                  respectant le RGPD.
                </li>
                <li>
                  Seules les données strictement nécessaires sont transmises.
                </li>
                <li>
                  Les données ne sont pas utilisées pour entraîner les modèles
                  lorsque cette option est disponible.
                </li>
                <li>
                  Sur simple demande, NMS06 fournit le nom du service IA utilisé
                  pour la prestation concernée.
                </li>
              </ul>
            </section>

            {/* 6 */}
            <section id="section-6" className="privacy-section">
              <h2>6. Conservation des données</h2>
              <p>Les données sont conservées :</p>
              <ul>
                <li>Pendant la durée de la relation associative</li>
                <li>Ou supprimées immédiatement sur demande</li>
                <li>Les données enfants : conservation maximale d’un an</li>
              </ul>
            </section>

            {/* 7 */}
            <section id="section-7" className="privacy-section">
              <h2>7. Vos droits</h2>
              <p>Conformément au RGPD, vous disposez des droits suivants :</p>
              <ul>
                <li>Droit d&apos;accès à vos données</li>
                <li>Droit de rectification</li>
                <li>Droit à l&apos;effacement</li>
                <li>Droit d’opposition</li>
                <li>Droit à la portabilité</li>
                <li>Droits renforcés concernant les mineurs</li>
              </ul>
              <p>
                Pour exercer vos droits :{" "}
                <a href="mailto:secretariat@nms06.fr">
                  secretariat@nms06.fr
                </a>
              </p>
            </section>

            {/* 8 */}
            <section id="section-8" className="privacy-section">
              <h2>8. Cookies</h2>
              <p>
                Notre site utilise des cookies techniques, statistiques et
                marketing. Un bandeau de consentement vous permet d&apos;accepter,
                refuser ou personnaliser leur utilisation.
              </p>
            </section>

            {/* 9 */}
            <section id="section-9" className="privacy-section">
              <h2>9. Sécurité</h2>
              <p>
                Nous mettons en œuvre toutes les mesures techniques et
                organisationnelles nécessaires pour protéger vos données
                (chiffrement SSL, serveurs sécurisés, accès restreints, etc.).
              </p>
            </section>

            {/* 10 */}
            <section id="section-10" className="privacy-section">
              <h2>10. Contact</h2>
              <p>Pour toute demande d&apos;information concernant vos données :</p>
              <ul>
                <li>Email : secretariat@nms06.fr</li>
                <li>Adresse : 251 avenue de la Porte des Alpes, 06670 Levens</li>
              </ul>
            </section>
          </div>
        </main>
      </div>
    </div>
  );
}
